import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import DoorFrontIcon from '@mui/icons-material/SensorDoor';
import type { VirtualRoomPainterPlugin } from '../VirtualRoomPainterPlugin';
import type { VirtualRoom } from '../services/VirtualRoomStorage';

/**
 * Places a VR's spawn point (`metadata.spawnPoint`, SC-422). Shown under the selected VR's row in
 * the room list. "Place" puts the editor in placement mode; the next map click picks the tile,
 * and "Save" writes it through Cloud Code at once.
 */
export const SpawnPointSection: React.FC<{ plugin: VirtualRoomPainterPlugin; room: VirtualRoom }> = ({ plugin, room }) => {
  const [placing, setPlacing] = useState(false);
  const [tile, setTile] = useState<{ x: number; y: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const isCubicle = room.kind === 'cubicle';
  const spawnPoint = room.metadata?.spawnPoint;

  useEffect(() => {
    const handleTilePlaced = ({ tile: placed }: { tile: { x: number; y: number } }) => {
      setTile(placed);
      setError(null);
    };
    const handleDone = () => {
      setPlacing(false);
      setTile(null);
      setError(null);
    };
    plugin.on('spawnPointDraftTilePlaced', handleTilePlaced);
    plugin.on('spawnPointDraftCancelled', handleDone);
    plugin.on('spawnPointCreated', handleDone);
    return () => {
      plugin.off('spawnPointDraftTilePlaced', handleTilePlaced);
      plugin.off('spawnPointDraftCancelled', handleDone);
      plugin.off('spawnPointCreated', handleDone);
    };
  }, [plugin]);

  // Selecting another VR mid-placement abandons this one's draft.
  useEffect(() => {
    return () => {
      if (plugin.getPendingSpawnPointDraft()?.virtualRoomId === room.id) plugin.cancelPlacingSpawnPoint();
    };
  }, [plugin, room.id]);

  const start = () => {
    setPlacing(true);
    setTile(null);
    setError(null);
    setWarning(null);
    plugin.beginPlacingSpawnPoint(room.id);
  };

  const cancel = () => {
    plugin.cancelPlacingSpawnPoint();
    setPlacing(false);
    setTile(null);
    setError(null);
  };

  const save = async () => {
    if (!tile) return;
    setSaving(true);
    setError(null);
    setWarning(null);
    try {
      const result = await plugin.confirmSpawnPoint();
      if (!result.success) {
        setError(result.reason || 'Failed to save spawn point.');
        return;
      }
      // Saved, but far from the room: say so without undoing the save.
      if (result.warning) setWarning(result.warning);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid={`spawn-point-section-${room.id}`}>
      <Typography variant="caption" color="text.secondary" data-testid="spawn-point-status">
        {spawnPoint
          ? `Spawn point: tile (${spawnPoint.x}, ${spawnPoint.y}). People teleporting here land on it.`
          : "No spawn point yet. People can't teleport to this room until it has one."}
      </Typography>

      {!placing && (
        <Box>
          <Button variant="outlined" size="small" startIcon={<DoorFrontIcon />} onClick={start}>
            {spawnPoint ? 'Move Spawn Point' : 'Place Spawn Point'}
          </Button>
        </Box>
      )}

      {placing && (
        <>
          <Alert severity="info">
            {tile
              ? `Tile (${tile.x}, ${tile.y}). Save it, or click another tile to move it.`
              : isCubicle
                ? 'Click a tile on the map, inside or next to this cubicle.'
                : 'Click a tile on the map, just outside this room.'}
          </Alert>
          {error && <Alert severity="error">{error}</Alert>}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={save} disabled={!tile || saving}>
              {saving ? 'Saving...' : 'Save Spawn Point'}
            </Button>
            <Button variant="text" size="small" onClick={cancel} disabled={saving}>
              Cancel
            </Button>
          </Box>
        </>
      )}
      {warning && <Alert severity="warning">{warning}</Alert>}
    </Box>
  );
};

export default SpawnPointSection;
